<template>
  <div class="input" :class="props.rootClassModifier">
    <div v-if="props.label || props.infoHeading || props.infoContent" class="input__label-wrapper">
      <label v-if="props.label" class="label" :for="inputId">{{ props.label }}</label>
      <Modal
        v-if="props.infoHeading || props.infoContent"
        :heading="props.infoHeading"
        :content="props.infoContent"
      >
        <template #link="{ clicked }">
          <button
            class="input__info-icon info-icon"
            type="button"
            :aria-label="infoButtonAriaLabel"
            @click="clicked">?</button>
        </template>
      </Modal>
    </div>
    <div :class="['input-group', { 'input-group--has-addon': !!props.unit }]">
      <input
        class="input__field input-group__input-field"
        :class="[
          { 'input__field--invalid': isFieldInvalid },
          { 'input-group__input-field--invalid': isFieldInvalid },
          { 'input-group__input-field--read-only': props.readOnly },
        ]"
        :id="inputId"
        :value="props.modelValue"
        :type="props.type"
        :readonly="props.readOnly"
        :placeholder="props.placeholder"
        :aria-invalid="isFieldInvalid || undefined"
        :aria-describedby="describedBy"
        @input="onInput"
        @blur="onBlur" />
      <span
        v-if="props.unit"
        class="input-group__input-field-addon"
        :class="[
          { 'input-group__input-field-addon--invalid': isInvalid },
          { 'input-group__input-field-addon--read-only': props.readOnly },
        ]">
        {{ props.unit }}
      </span>
    </div>
    <p
        v-if="hasErrorMessage"
        :id="errorId"
        class="input__error"
        aria-live="polite">
      {{ currentErrorMessage }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, useId } from "vue";
import Modal from "@shared/Components/Modal/Modal.vue";
import { getTranslations } from "@shared/Services/translations";

const props = withDefaults(
  defineProps<{
    modelValue: string;
    type?: string;
    placeholder?: string;
    rootClassModifier?: string;
    label?: string;
    id?: string;
    isInvalid?: boolean;
    readOnly?: boolean;
    infoHeading?: string;
    infoContent?: string;
    unit?: string;
    errorMessage?: string;
  }>(),
  {
    type: "text",
    placeholder: "",
    rootClassModifier: "",
    isInvalid: false,
    readOnly: false,
    infoHeading: "",
    infoContent: "",
    unit: "",
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "input", value: string): void;
  (e: "blur"): void;
}>();

const translations = getTranslations();
const infoButtonAriaLabel = computed(() => {
  const infoButtonText = translations["bookings.common.openinformation"] ?? "Open information";
  const subject = props.infoHeading || props.label;

  return subject ? `${infoButtonText}: ${subject}` : infoButtonText;
});

const generatedId = useId();
const inputId = computed(() => props.id || generatedId);
const errorId = computed(() => `${inputId.value}-error`);
const currentErrorMessage = computed(() => props.errorMessage?.trim() ?? "");
const hasErrorMessage = computed(() => currentErrorMessage.value.length > 0);
const isFieldInvalid = computed(() => props.isInvalid || hasErrorMessage.value);
const describedBy = computed(() => (hasErrorMessage.value ? errorId.value : undefined));

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit("update:modelValue", target.value);
  emit("input", target.value);
};

const onBlur = () => {
  emit("blur");
};
</script>
